import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  SelectHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { ResultItem } from './result-item/ResultItem'
import { IOption } from '../search-input/BpSearchInput'
import { usePreviousValue } from '../../../lib/hooks/UsePreviousValue'
import { Preloader } from '../../cards/preloader/Preloader'
import { useTranslation } from 'react-i18next'

export type TResultValue = string | ReadonlyArray<string> | number | undefined

const DEFAULT_OPTION: IOption = { text: '', value: '' }

export interface IResultDropProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options?: IOption[]
  search?: string
  onSelected?: (value: IOption | IOption[] | undefined) => void
  noShadow?: boolean
  pinTop?: boolean
  asList?: boolean
  loading?: boolean
  hasTranslate?: boolean
  required?: boolean
  /** Своя отрисовка опции в списке; label — подпись с переводом и подсветкой поиска */
  optionRender?: (option: IOption, label: ReactNode) => ReactNode
}

export const ResultDrop = forwardRef(
  (props: IResultDropProps, ref: ForwardedRef<HTMLSelectElement>) => {
    const { t } = useTranslation()
    const { t: translateOption } = useTranslation('translation', { keyPrefix: 'fields.options' })
    const {
      options = [],
      search = '',
      noShadow = false,
      pinTop = false,
      asList = false,
      onSelected,
      value,
      loading = false,
      multiple,
      defaultValue,
      hasTranslate = false,
      required,
      optionRender,
      ...selectAttributes
    } = props

    const [currentValue, setCurrentValue] = useState<TResultValue>()
    const [selectedOption, setSelectedOption] = useState<IOption>()
    const [selectedOptions, setSelectedOptions] = useState<IOption[]>([])
    const [results, setResults] = useState<IOption[]>([])
    const [visibleResults, setVisibleResults] = useState<IOption[]>([])
    const previousValue = usePreviousValue(value)
    const previousOptions = usePreviousValue(options)
    const selectRef = useRef<any>(null)
    useImperativeHandle(ref, () => selectRef.current)

    // Закреплённые опции всегда стоят первыми — выше и выбранных, и найденных поиском
    const toVisibleResults = (list: IOption[]): IOption[] =>
      list
        .filter(({ allowDelete = true }) => allowDelete)
        .sort((a, b) => (!a.pinned === !b.pinned ? 0 : a.pinned ? -1 : 1))

    useEffect(() => {
      selectCurrentValues(defaultValue)
    }, [])

    useEffect(() => {
      if (JSON.stringify(options) === JSON.stringify(previousOptions)) return
      setResults(options)
      setVisibleResults(toVisibleResults(options))
    }, [options])

    useEffect(() => {
      if (currentValue === undefined) return
      if (onSelected) {
        Array.isArray(currentValue) ? onSelected(selectedOptions) : onSelected(selectedOption)
      }
      if (pinTop) pinTopSelected()

      selectRef.current.dispatchEvent(new Event('change', { bubbles: true }))
    }, [currentValue])

    useEffect(() => {
      if (JSON.stringify(value) === JSON.stringify(previousValue)) return
      selectCurrentValues(value)
    }, [value])

    useEffect(() => {
      if (search) {
        const searchTerm = search.toLowerCase().replace('.', '')

        setVisibleResults(
          toVisibleResults(
            options
              .filter((option) => option.text.toLowerCase().includes(searchTerm))
              .sort((a, b) => {
                const aText = a.text.toLowerCase().replace('.', '')
                const bText = b.text.toLowerCase().replace('.', '')

                const isExactStart = (text: string) =>
                  text === searchTerm ||
                  text.startsWith(searchTerm + ' ') ||
                  text.startsWith(searchTerm + '(')

                const aExact = isExactStart(aText) ? 0 : 1
                const bExact = isExactStart(bText) ? 0 : 1

                if (aExact !== bExact) return aExact - bExact

                const aIndex = aText.indexOf(searchTerm)
                const bIndex = bText.indexOf(searchTerm)

                return aIndex - bIndex || aText.length - bText.length
              }),
          ),
        )
      } else {
        setVisibleResults(toVisibleResults(options))
      }
    }, [search])

    useEffect(() => {
      setCurrentValue(Array.from(selectedOptions.map((o) => o.value)))
    }, [selectedOptions])

    useEffect(() => {
      setCurrentValue(selectedOption?.value)
    }, [selectedOption])

    useEffect(() => {
      if (pinTop) pinTopSelected()
    }, [pinTop])

    const setMultipleValues = (option: IOption) => {
      if (selectedOptions.find((o) => o.value == option.value)) {
        setSelectedOptions(selectedOptions.filter((o) => o.value !== option.value))
      } else {
        setSelectedOptions([...selectedOptions, option])
      }
    }

    const setSingleValue = (option: IOption) => {
      if (selectedOption === option) {
        setSelectedOption(undefined)
      } else {
        setSelectedOption(option)
      }
    }

    const handleOnClick = (option: IOption, isSelected: boolean) => {
      if (asList) {
        if (onSelected) onSelected(option)
        return
      }

      if (defaultValue && isSelected && !multiple) {
        return
      }

      if (multiple) {
        setMultipleValues(option)
      } else {
        if (isSelected) option = { text: '', value: '' }
        setSingleValue(option)
      }
    }

    const isSelected = (option: IOption): boolean => {
      if (asList) return false
      if (multiple) {
        return !!selectedOptions.find((o) => o.value == option.value)
      } else {
        return option.value === selectedOption?.value
      }
    }

    const pinTopSelected = () => {
      const res = [...options].sort((a: IOption, b: IOption) => {
        if (
          multiple
            ? selectedOptions.find((o) => o.value === a.value)
            : selectedOption?.value === a.value
        ) {
          return -1
        } else if (
          multiple
            ? selectedOptions.find((o) => o.value === b.value)
            : selectedOption?.value === b.value
        ) {
          return 1
        }

        return 0
      })

      setVisibleResults(toVisibleResults(res))
    }

    const selectCurrentValues = (value: string | ReadonlyArray<string> | number | undefined) => {
      if (Array.isArray(value)) {
        setSelectedOptions(options?.filter((o: IOption) => value.includes(o.value)))
      } else if (multiple && selectedOptions.length) {
        // значение сбросили снаружи не массивом — например setValue(field, undefined)
        setSelectedOptions([])
      }

      if (multiple) return

      if (value) {
        setSelectedOption(options?.find((o: IOption) => o.value == value))
      } else if (defaultValue && defaultValue !== '') {
        setSelectedOption(options?.find((o: IOption) => o.value == defaultValue))
      } else if (!asList) {
        setSelectedOption(DEFAULT_OPTION)
      }
    }

    return (
      <>
        <div
          className={`relative flex flex-col overflow-auto bg-white p-[8px] ${
            (!results || (!!results && results.length <= 0)) &&
            'min-h-[88px] items-center justify-center'
          } ${loading && 'h-[88px]'} ${!noShadow && 'shadow-outline shadow-default'} `}
          style={{ overscrollBehaviorY: 'none' }}
        >
          {loading && (
            <div className={'flex justify-center'}>
              <div className={'relative w-[32px] py-[32px]'}>
                <Preloader />
              </div>
            </div>
          )}
          {!loading &&
            visibleResults.length > 0 &&
            visibleResults
              .filter((option) => !option.hidden)
              .map((option: IOption) => (
                <ResultItem
                  key={option.value}
                  found={search}
                  multiple={multiple}
                  render={optionRender && ((label) => optionRender(option, label))}
                  required={required}
                  selected={isSelected(option)}
                  onClick={() => handleOnClick(option, isSelected(option))}
                >
                  {hasTranslate ? translateOption(option.text) : option.text}
                </ResultItem>
              ))}
          {!loading && !visibleResults.length && (
            <div className='py-[32px] text-center text-[14px] text-aqua-300'>
              {t('other.noResultsFound')}
            </div>
          )}
        </div>
        <select
          {...selectAttributes}
          ref={selectRef}
          hidden
          multiple={multiple}
          value={currentValue}
        >
          <option value=''></option>
          {results?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.text}
            </option>
          ))}
        </select>
      </>
    )
  },
)
